import { useCallback, useEffect, useState } from 'react';
import { api, fmt, type User } from '../api.ts';
import BoardStock from './BoardStock.tsx';
import ReelStock from './ReelStock.tsx';

type Tab = 'board' | 'reels' | 'consumables';

interface ConsumableRow {
  consumable_id: number;
  name: string;
  category: string;
  unit: string;
  quantity_on_hand: string;
}


export default function Stock({ user, view }: { user: User; view: Tab }) {
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const canManage = user.role === 'ADMIN' || user.role === 'MANAGER';

  useEffect(() => { setError(''); setNotice(''); }, [view]);

  return (
    <div className="panel">
      {error && <div className="error" style={{ margin: 16 }}>{error}</div>}
      {notice && <div style={{ margin: 16 }} className="tag ready notice">{notice}</div>}

      {view === 'board' && (
        <BoardStock canManage={canManage} setError={setError} setNotice={setNotice} />
      )}
      {view === 'reels' && <ReelStock user={user} setError={setError} />}
      {view === 'consumables' && (
        <ConsumablesTab canManage={canManage} setError={setError} setNotice={setNotice} />
      )}
    </div>
  );
}

type Setters = { setError: (s: string) => void; setNotice: (s: string) => void };

function ConsumablesTab({ canManage, setError, setNotice }: Setters & { canManage: boolean }) {
  const [rows, setRows] = useState<ConsumableRow[]>([]);
  const [moveFor, setMoveFor] = useState<ConsumableRow | null>(null);
  const [type, setType] = useState<'PURCHASE' | 'ISSUE' | 'ADJUSTMENT'>('PURCHASE');
  const [qty, setQty] = useState('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setRows((await api.get<{ consumables: ConsumableRow[] }>('/api/stock/consumables')).consumables);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load stock');
    }
  }, [setError]);

  useEffect(() => { void load(); }, [load]);

  async function record() {
    if (!moveFor) return;
    setBusy(true); setError('');
    try {
      const res = await api.post<{ quantityOnHand: number }>(
        `/api/stock/consumables/${moveFor.consumable_id}/movements`,
        { movementType: type, quantity: Number(qty), ...(notes ? { notes } : {}) },
      );
      setNotice(`${moveFor.name}: ${fmt(res.quantityOnHand)} ${moveFor.unit} on hand`);
      setMoveFor(null); setQty(''); setNotes('');
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not record the movement');
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="stagebar">
        <span className="hint" style={{ alignSelf: 'center' }}>
          Gum, pins, chemicals and ink. Balance is the sum of purchases, issues and adjustments.
        </span>
      </div>

      <div className="tbl">
        <table>
          <thead>
            <tr>
              <th>Item</th><th>Category</th><th className="num">On hand</th><th>Unit</th><th />
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.consumable_id}>
                <td>{r.name}</td>
                <td className="dim">{r.category}</td>
                <td className="num"><strong>{fmt(r.quantity_on_hand)}</strong></td>
                <td className="dim">{r.unit}</td>
                <td>
                  <button className="btn" onClick={() => {
                    setMoveFor(r); setType('PURCHASE'); setQty(''); setNotes(''); setNotice('');
                  }}>Record movement</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {!canManage && (
        <div className="hint" style={{ padding: '10px 16px' }}>
          Adding a new stock item requires a manager.
        </div>
      )}

      {moveFor && (
        <div className="backdrop" onClick={() => setMoveFor(null)}>
          <div className="modal" style={{ width: 460 }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-head">
              <h3>{moveFor.name}</h3>
              <p>{fmt(moveFor.quantity_on_hand)} {moveFor.unit} on hand</p>
            </div>
            <div className="modal-body">
              <div className="field">
                <label>Movement</label>
                <div className="radio">
                  {(['PURCHASE', 'ISSUE', 'ADJUSTMENT'] as const).map((t) => (
                    <label key={t}>
                      <input type="radio" checked={type === t} onChange={() => setType(t)} />
                      {t === 'PURCHASE' ? 'Purchase' : t === 'ISSUE' ? 'Issue' : 'Adjustment'}
                    </label>
                  ))}
                </div>
              </div>
              <div className="field">
                <label htmlFor="mq" className="req">Quantity ({moveFor.unit})</label>
                <input id="mq" type="number" value={qty} autoFocus
                       onChange={(e) => setQty(e.target.value)} />
                <div className="hint">
                  {type === 'ADJUSTMENT'
                    ? 'Use a negative number to reduce stock, e.g. −5 for spillage'
                    : type === 'ISSUE'
                      ? 'Cannot exceed what is on hand'
                      : 'Amount received'}
                </div>
              </div>
              <div className="field" style={{ marginBottom: 0 }}>
                <label htmlFor="mn">Notes</label>
                <input id="mn" type="text" value={notes}
                       onChange={(e) => setNotes(e.target.value)} />
              </div>
            </div>
            <div className="modal-foot">
              <button className="btn" onClick={() => setMoveFor(null)} disabled={busy}>Cancel</button>
              <button className="btn primary" onClick={record}
                      disabled={busy || !qty || Number(qty) === 0}>
                {busy ? 'Saving…' : 'Record'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
